import React from 'react';
import { motion } from 'framer-motion';
import { images } from '../../utils/api';

const TITLE_FIELDS = ['country', 'year', 'mint_mark', 'assayer', 'denomination', 'variety'];

const DetailsView = ({ 
  coins, 
  isShared,
  setInfo,
  onImageClick 
}) => {
  if (!coins?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No coins in this set</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12"
    >
      <div className="space-y-8">
        {coins.map((coin) => (
          <div 
            key={coin.id}
            className="bg-white rounded-lg shadow overflow-hidden"
          >
            <div className="flex flex-col md:flex-row">
              {/* Left side - Large image */}
              <div className="md:w-1/2 relative">
                {coin.images?.length > 0 ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <img
                        src={images.getImage(coin.images[0].file_path, coin.images[0].source)}
                        alt={`${coin.year} ${coin.denomination}`}
                        className="w-full cursor-pointer"
                        onClick={() => onImageClick?.(0, coin)}
                      />
                    </div>
                    {coin.images.length > 1 && (
                      <button
                        onClick={() => onImageClick?.(0, coin)}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm transition-colors"
                      >
                        View All Images ({coin.images.length})
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="min-h-[300px] bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400">No image available</span>
                  </div>
                )}
              </div>

              {/* Right side - Coin details */}
              <div className="md:w-1/2 p-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {[
                      coin.country,
                      [
                        coin.year,
                        coin.mint_mark,
                        coin.assayer
                      ].filter(Boolean).join('-'),
                      coin.denomination,
                      coin.variety
                    ].filter(Boolean).join(' ')}
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {[
                      // Certification details
                      'grade',
                      'certification_service_id',
                      'certification_number',
                      // Physical attributes
                      'composition',
                      'weight',
                      'diameter',
                      'thickness',
                      'edge',
                      'designer',
                      'mintage',
                      // Population data
                      'population',
                      'population_higher',
                      'population_total',
                      // Purchase info
                      'purchase_date',
                      'purchase_price',
                      'list_price'
                    ].map(field => {
                      if (!setInfo?.field_preferences?.visible_fields?.includes(field)) return null;
                      const value = field === 'certification_service_id' ? coin.certification_service_name : coin[field];
                      if (!value) return null;
                      
                      let displayValue = value;
                      if (field === 'purchase_date') {
                        displayValue = new Date(value).toLocaleDateString();
                      } else if (field.includes('price') && !isNaN(value)) {
                        displayValue = `$${value}`;
                      } else if (field === 'weight') {
                        displayValue = `${value}g`;
                      } else if (field === 'diameter') {
                        displayValue = `${value}mm`;
                      } else if (field === 'mintage' && !isNaN(value)) {
                        displayValue = Number(value).toLocaleString();
                      } else if (field === 'grade' && coin.cac_sticker && coin.cac_sticker !== 'none') {
                        displayValue = (
                          <div className="flex items-center gap-2">
                            <span>{value}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                              coin.cac_sticker === 'gold' 
                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                : 'bg-green-100 text-green-800 border border-green-200'
                            }`}>
                              CAC
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={field} className="min-w-[200px]">
                          <dt className="text-sm font-medium text-gray-500">
                            {field === 'certification_service_id' ? 'TPG' : 
                             field === 'certification_number' ? 'Cert #' :
                             field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">{displayValue}</dd>
                        </div>
                      );
                    })}
                  </div>

                  {setInfo?.field_preferences?.visible_fields?.includes('notes') && coin.notes && (
                    <div className="mt-6">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Notes</dt>
                      <dd className="text-sm text-gray-900 bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">{coin.notes}</dd>
                    </div>
                  )}

                  {setInfo?.field_preferences?.visible_fields?.includes('coin_details') && coin.coin_details && (
                    <div className="mt-6">
                      <dt className="text-sm font-medium text-gray-500 mb-1">Details</dt>
                      <dd className="text-sm text-gray-900">
                        <div 
                          className="prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-ul:pl-5 prose-ol:pl-5 prose-li:my-0 prose-p:my-0 bg-gray-50 rounded-lg p-4"
                          dangerouslySetInnerHTML={{ __html: coin.coin_details }}
                        />
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default DetailsView;
